// src/ds/format.ts — formatting helpers (ZAR edition). Canonical money()/relativeTime()
// for the whole console; never re-roll number/currency/time formatting in a page.
import type { Currency } from "./types";

const SYMBOL: Record<Currency, string> = { ZAR: "R", USD: "$", USDC: "" };

/** R1,515,600.00 · $24,000.00 · 30,000.00 USDC — tabular figures, currency-aware. */
export function money(amount: number, currency: Currency): string {
  const n = amount.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency === "USDC" ? `${n} USDC` : `${SYMBOL[currency]}${n}`;
}

/** Relative time. Prototype stores `mins`; production passes an ISO string. */
export function relativeTime(input: number | string): string {
  const mins =
    typeof input === "number"
      ? input
      : Math.max(0, Math.round((Date.now() - Date.parse(input)) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

/** Signed milliseconds from `now` to `input`: positive ahead, negative once passed. */
export function msUntil(input: string, now: number = Date.now()): number {
  return Date.parse(input) - now;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Time left until a FUTURE time, as a live countdown: 23:41, or 2:05:07 past an
 * hour. relativeTime() is past-only (it clamps a future time to zero and says
 * "ago"), so an expiry always goes through here. A part-second rounds up, so the
 * display reads 00:00 only once the time has actually come; a past time is 00:00.
 */
export function countdown(input: string, now: number = Date.now()): string {
  const ms = msUntil(input, now);
  // An unparseable time shows as 00:00, never NaN:NaN.
  const total = Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 1000)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return hours > 0
    ? `${hours}:${pad2(minutes)}:${pad2(seconds)}`
    : `${pad2(minutes)}:${pad2(seconds)}`;
}

export const initials = (name: string): string => {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? p[0]?.[1] ?? "")).toUpperCase();
};

/** Access-controlled masking (UX only — the API must withhold PII server-side). */
export const maskRef = (ref: string): string =>
  `${ref.slice(0, 4)} •••• ••${ref.replace(/\s+/g, "").slice(-2)}`;
export const maskEmail = (e: string): string => {
  const at = e.indexOf("@");
  return at < 1 ? "••••••" : `${e[0]}••••@••••${e.slice(e.lastIndexOf("."))}`;
};
